import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';
import enLocale from '../locales/en.json';
import esLocale from '../locales/es.json';

const locales = { en: enLocale, es: esLocale };

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: 'white',
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  text: {
    fontSize: 12,
    textAlign: 'justify',
    lineHeight: 1.5,
    width: '100%',
    flexWrap: 'wrap',
  },
  bold: {
    fontWeight: 'bold',
  },
  heading1: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  heading2: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  heading3: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  logo: {
    width: 150,
    height: 'auto',
    marginTop: 20,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 10,
  },
  actionItemsSection: {
    marginTop: 20,
    paddingTop: 15,
    borderTop: 1,
    borderTopColor: '#023E5B',
  },
  actionItemsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#023E5B',
    marginBottom: 12,
    textAlign: 'center',
  },
  actionItem: {
    flexDirection: 'row',
    marginBottom: 8,
    paddingLeft: 10,
  },
  actionItemNumber: {
    width: 20,
    fontSize: 12,
    fontWeight: 'bold',
    color: '#023E5B',
  },
  actionItemContent: {
    flex: 1,
    fontSize: 11,
    lineHeight: 1.4,
  },
  actionItemTitleText: {
    fontWeight: 'bold',
    color: '#023E5B',
  },
  actionItemDescription: {
    color: '#333',
  },
});

const renderMarkdown = (text) => {
  const elements = [];
  const lines = text.split('\n');

  lines.forEach((line, index) => {
    // Check for headings
    if (line.startsWith('# ')) {
      elements.push(<Text key={`h1-${index}`}>{'\n'}</Text>);
      elements.push(
        <Text key={`h1-text-${index}`} style={styles.heading1}>
          {line.slice(2)}
        </Text>
      );
    } else if (line.startsWith('## ')) {
      elements.push(<Text key={`h2-${index}`}>{'\n'}</Text>);
      elements.push(
        <Text key={`h2-text-${index}`} style={styles.heading2}>
          {line.slice(3)}
        </Text>
      );
    } else if (line.startsWith('### ') || line.startsWith('#### ')) {
      elements.push(<Text key={`h3-${index}`}>{'\n'}</Text>);
      elements.push(
        <Text key={`h3-text-${index}`} style={styles.heading3}>
          {line.startsWith('#### ') ? line.slice(5) : line.slice(4)}
        </Text>
      );
    } else {
      // Handle bold text
      const lineElements = [];
      const regex = /\*\*(.*?)\*\*/g;
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(line)) !== null) {
        const boldText = match[1];
        const boldStartIndex = match.index;
        const boldEndIndex = regex.lastIndex;

        // Add text before bold segment
        if (boldStartIndex > lastIndex) {
          lineElements.push(
            <Text key={`${index}-${lastIndex}`} style={styles.text}>
              {line.substring(lastIndex, boldStartIndex)}
            </Text>
          );
        }

        // Add bold text
        lineElements.push(
          <Text key={`${index}-bold-${boldStartIndex}`} style={[styles.text, styles.bold]}>
            {boldText}
          </Text>
        );

        lastIndex = boldEndIndex;
      }

      // Add remaining text after last bold segment
      if (lastIndex < line.length) {
        lineElements.push(
          <Text key={`${index}-last`} style={styles.text}>
            {line.substring(lastIndex)}
          </Text>
        );
      }

      // If there were no bold matches, just add the whole line
      if (lineElements.length === 0) {
        lineElements.push(
          <Text key={`line-${index}`} style={styles.text}>
            {line}
          </Text>
        );
      }

      // Combine line elements
      elements.push(
        <Text key={`line-${index}`} style={styles.text}>
          {lineElements}
        </Text>
      );
    }

    // Add line breaks between lines
    if (index !== lines.length - 1) {
      elements.push(<Text key={`break-${index}`}>{'\n'}</Text>);
    }
  });

  return elements;
};

const MyDocument = ({ report, nda_report, disclaimer, language = 'en' }) => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Get action items from locale files
  const locale = locales[language] || locales.en;
  const actionItems = {
    title: locale.actionItems.title,
    items: Object.values(locale.actionItems.items),
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src="/LOVColoredBG.png" style={styles.logo} />
        </View>
        <View style={styles.section}>
          <Text style={styles.heading2}>Report Details</Text>
          <Text>{'\n'}</Text>
          {renderMarkdown(report)}

          {nda_report && (
            <React.Fragment>
              <Text>{'\n'}</Text>
              <Text>{'\n'}</Text>
              <Text style={styles.heading3}>Additional NDA Information</Text>
              {renderMarkdown(nda_report)}
            </React.Fragment>
          )}
          <Text style={styles.text}>{renderMarkdown(disclaimer)}</Text>

          {/* Action Items Section */}
          <View style={styles.actionItemsSection}>
            <Text style={styles.actionItemsTitle}>{actionItems.title}</Text>
            {actionItems.items.map((item, index) => (
              <View key={index} style={styles.actionItem}>
                <Text style={styles.actionItemNumber}>{index + 1}.</Text>
                <Text style={styles.actionItemContent}>
                  <Text style={styles.actionItemTitleText}>{item.title}</Text>
                  {' '}
                  <Text style={styles.actionItemDescription}>{item.description}</Text>
                </Text>
              </View>
            ))}
          </View>

          <Text style={styles.footer}>
            Report provided by Lift Our Voices on {currentDate}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default MyDocument;
